<template>
    <div class="relative w-full max-w-md mx-auto sm:max-w-lg md:max-w-xl lg:max-w-2xl">
        <!-- Champ de recherche principal -->
        <div class="relative">
            <label
                class="flex flex-row sm:flex-row items-stretch sm:items-center justify-between border border-gray-300 rounded-sm px-4 py-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-blue-400 focus-within:border-blue-400 transition-all cursor-pointer bg-white w-full"
            >
                <div class="flex items-center flex-1 w-full mb-2 sm:mb-0">
                    <!-- Icône de recherche -->
                    <svg class="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <input
                        type="text"
                        placeholder="Rechercher un livre, auteur..."
                        class="flex-1 outline-none text-sm text-gray-700 placeholder-gray-500 w-full"
                        @focus="openModal"
                        @click="openModal"
                        readonly
                    />
                </div>
                <!-- Badge Ctrl+K -->
                <div class="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded px-2 py-1 text-xs font-medium text-gray-600 transition-colors self-start sm:self-center">
                    <kbd class="font-mono">Ctrl</kbd>
                    <span>+</span>
                    <kbd class="font-mono">K</kbd>
                </div>
            </label>
        </div>

        <!-- Modal -->
        <div
            v-if="showModal"
            class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            @click="closeModal"
        >
            <div
                class="bg-white rounded-lg shadow-2xl w-full max-w-full sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
                @click.stop
            >
                <!-- Header modal -->
                <div class="border-b border-gray-200 p-6">
                    <div class="flex flex-row sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                        <div class="flex items-center gap-2">
                            <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253z"></path>
                            </svg>
                            <h3 class="text-xl font-semibold text-gray-900">Recherche de livres</h3>
                        </div>
                        <button
                            @click="closeModal"
                            class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-colors self-start sm:self-center"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>

                    <!-- Input modal -->
                    <div class="relative mb-4">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                        </div>
                        <input
                            ref="modalSearchInput"
                            type="text"
                            v-model="search"
                            placeholder="Titre, auteur, mots-clés, résumé... (min. 3 caractères)"
                            class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none text-sm"
                        />
                    </div>

                    <!-- Filtres -->
                    <div class="flex flex-wrap gap-2">
                        <button
                            v-for="filter in filters"
                            :key="filter.id"
                            @click="toggleFilter(filter.id)"
                            :class="[
                'px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                activeFilters.includes(filter.id)
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 '
              ]"
                        >
                            {{ filter.label }}
                        </button>
                    </div>
                </div>

                <!-- Résultats -->
                <div class="flex-1 overflow-y-auto max-h-[60vh] p-6">
                    <!-- Messages d'erreur / loading / vide -->
                    <div v-if="searchError" class="flex flex-col items-center justify-center py-12 text-red-500">
                        <svg class="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <p class="text-lg font-medium mb-2">Erreur lors de la recherche</p>
                        <p class="text-sm text-gray-600">{{ searchError }}</p>
                    </div>

                    <div v-else-if="isLoading" class="flex flex-col items-center justify-center py-12 text-gray-500">
                        <svg class="w-8 h-8 animate-spin mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <p class="text-sm">Recherche en cours...</p>
                    </div>

                    <div v-else-if="search && search.length < 3" class="flex flex-col items-center justify-center py-12 text-gray-400">
                        <svg class="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                        </svg>
                        <p class="text-lg font-medium mb-2">Continuez à taper</p>
                        <p class="text-sm">Entrez au moins 3 caractères pour rechercher</p>
                    </div>

                    <div v-else-if="results.length === 0 && search" class="flex flex-col items-center justify-center py-12 text-gray-500">
                        <svg class="w-12 h-12 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.239 0-4.291.94-5.74 2.46"></path>
                        </svg>
                        <p class="text-lg font-medium mb-2">Aucun résultat trouvé</p>
                        <p class="text-sm">pour "{{ search }}"</p>
                    </div>

                    <div v-else-if="results.length === 0" class="flex flex-col items-center justify-center py-12 text-gray-400">
                        <svg class="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253z"></path>
                        </svg>
                        <p class="text-lg font-medium mb-2">Découvrez notre bibliothèque</p>
                        <p class="text-sm">Commencez à taper pour rechercher des livres</p>
                    </div>

                    <!-- Résultats groupés -->
                    <div v-else class="space-y-8">
                        <div v-for="(books, bookType) in groupedResults" :key="bookType" class="space-y-4">
                            <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 border-b border-gray-100 gap-2">
                                <div class="flex items-center gap-2">
                                    <div class="w-1 h-6 bg-blue-500 rounded-md"></div>
                                    <h4 class="text-lg font-bold text-gray-800 capitalize">{{ bookType || 'Non classé' }}</h4>
                                </div>
                                <span class="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-full">{{ books.length }}</span>
                            </div>

                            <div class="grid gap-4">
                                <div
                                    v-for="book in books"
                                    :key="book.id"
                                    class="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden group"
                                    @click="navigationTo(book)"
                                >
                                    <div class="flex flex-col sm:flex-row gap-4 p-4">
                                        <div class="flex-shrink-0 w-full sm:w-24 h-32 bg-gray-100 rounded-lg overflow-hidden shadow-sm">
                                            <img
                                                :src="book.metadata['banner'] || props.defaultBanner || '/images/default-book.png'"
                                                :alt="book.metadata['title'] || 'Couverture du livre'"
                                                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                @error="handleImageError"
                                            />
                                        </div>

                                        <div class="flex-1 min-w-0 flex flex-col">
                                            <h2
                                                class="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors"
                                                v-html="highlightText(book.metadata['title'], search)"
                                            ></h2>

                                            <div class="flex flex-wrap items-center gap-4 mb-3 text-sm text-gray-600">
                                                <div v-if="book.metadata['auteur']" class="flex items-center gap-1">
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                                    </svg>
                                                    <span v-html="highlightText(book.metadata['auteur'], search)"></span>
                                                </div>
                                                <div v-if="book.metadata['publication_date']" class="flex items-center gap-1">
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                                    </svg>
                                                    <span>{{ book.metadata['publication_date'] }}</span>
                                                </div>
                                            </div>

                                            <p class="text-sm text-gray-700 line-clamp-2 leading-relaxed mb-3" v-html="highlightText(book.metadata['description'], search)"></p>

                                            <div class="flex flex-wrap gap-1" v-if="book.metadata['mots_cles'] && book.metadata['mots_cles'].length">
                        <span
                            v-for="tag in book.metadata['mots_cles'].slice(0, 3)"
                            :key="tag"
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                            v-html="highlightText(tag, search)"
                        ></span>
                                                <span
                                                    v-if="book.metadata['mots_cles'].length > 3"
                                                    class="inline-flex items-center px-2 py-1 rounded-full text-xs text-gray-500 bg-gray-50"
                                                >
                          +{{ book.metadata['mots_cles'].length - 3 }}
                        </span>
                                            </div>
                                        </div>

                                        <div class="flex-shrink-0 flex items-center mt-2 sm:mt-0">
                                            <svg class="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination Footer -->
                <div  class="border-t border-gray-200 px-6 py-4 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <div class="text-sm text-gray-600 font-medium">
                        <span class="text-blue-600">{{ totalForSearch }}</span> résultats trouvés
                    </div>
                    <div class="flex gap-2">
                        <a
                           :href="voirTous()"
                            class="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-white hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Voir tous
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, watch, nextTick, onMounted, onBeforeUnmount} from 'vue'
import axios from 'axios'
import {route} from "@/helper.js";

const props = defineProps({
    defaultBanner: {
        type: String,
        default: '/images/default-book.png'
    },
})

// État réactif
const search = ref('')
const showModal = ref(false)
const results = ref([])
const isLoading = ref(false)
const searchError = ref('')
const activeFilters = ref([])
const modalSearchInput = ref(null)

// Filtres disponibles
const filters = ref([
    {id: 'Monographie', label: 'Monographie'},
    {id: 'Travaux', label: 'Travaux'},
    {id: 'Periodique', label: 'Periodique'},
])

let searchTimer = null

// Computed properties
const totalPages = computed(() => Math.ceil(results.value.length / itemsPerPage))

const groupedResults = computed(() => {
    const groups = {}

    results.value.forEach(book => {
        const bookType = book.book_type || 'Non classé'
        if (!groups[bookType]) {
            groups[bookType] = []
        }
        groups[bookType].push(book)
    })

    return groups
})

// Méthodes
const openModal = async () => {
    showModal.value = true
    await nextTick()
    if (modalSearchInput.value) {
        modalSearchInput.value.focus()
    }
}

const closeModal = () => {
    showModal.value = false
    search.value = ''
    results.value = []
    searchError.value = ''
}

const toggleFilter = (filterId) => {
    const index = activeFilters.value.indexOf(filterId)
    if (index > -1) {
        activeFilters.value.splice(index, 1)
    } else {
        activeFilters.value.push(filterId)
    }
    performSearch()
}
const totalForSearch = ref(0)

const performSearch = () => {
    // Réinitialiser l'erreur
    searchError.value = ''

    if (!search.value.trim()) {
        results.value = []
        isLoading.value = false
        return
    }

    if (search.value.length < 3) {
        results.value = []
        isLoading.value = false
        return
    }

    isLoading.value = true

    axios.get(`/api/book/search`, {
        params: {
            q: search.value,
            filters: activeFilters.value.join(',')
        }
    })
        .then((response) => {
            results.value = response.data.result
            totalForSearch.value = response.data.total
            isLoading.value = false
        })
        .catch((error) => {
            console.error('Erreur lors de la recherche:', error)
            searchError.value = error.response?.data?.message || 'Une erreur est survenue lors de la recherche. Veuillez réessayer.'
            results.value = []
            isLoading.value = false
        })
}

const escapeHtml = (text) => {
    if (!text) return ''
    return String(text).replace(/[&<>"']/g, (char) => {
        const entities = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        }
        return entities[char]
    })
}

const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return escapeHtml(text)

    const escaped = escapeHtml(text)
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')

    return escaped.replace(regex, '<span class="bg-yellow-300 text-black font-medium px-1 rounded">$1</span>')
}

const handleImageError = (event) => {
    event.target.src = props.defaultBanner || '/images/default-book.png'
}

const navigationTo = (book) => {
    window.location.href = route('show-book', { book:book.id, slug: slugify(book?.metadata?.title) });
}

function slugify(text) {
    return text
        .toString()
        .normalize('NFD')               // Sépare les accents
        .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
        .toLowerCase()                   // Minuscule
        .trim()                          // Supprime les espaces au début/fin
        .replace(/[^a-z0-9 -]/g, '')     // Supprime les caractères spéciaux
        .replace(/\s+/g, '-')            // Remplace espaces par tiret
        .replace(/-+/g, '-')
        .replace(/-+$/, '');            // Supprime tirets multiples
}

// Watchers
watch(search, (newVal) => {
    clearTimeout(searchTimer)

    if (newVal.trim() === '') {
        results.value = []
        isLoading.value = false
        searchError.value = ''
        return
    }

    if (newVal.length < 3) {
        results.value = []
        isLoading.value = false
        return
    }

    searchTimer = setTimeout(() => {
        performSearch()
    }, 100)
})

watch(activeFilters, () => {
    if (search.value.trim() && search.value.length >= 3) {
        performSearch()
    }
}, {deep: true})

// Gestion du clavier
const handleKeydown = (e) => {
    if (e.ctrlKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        e.stopPropagation()
        showModal.value = !showModal.value
        if (showModal.value) {
            nextTick(() => {
                if (modalSearchInput.value) {
                    modalSearchInput.value.focus()
                }
            })
        }
    }
    if (e.key === "Escape" && showModal.value) {
        closeModal()
    }
}

const voirTous = () => {
    return window.location.origin + '/searchView?search=' + search.value + '&filters=' + activeFilters.value.join(',')
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
    clearTimeout(searchTimer)
    document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-4 {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
